import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { EventService } from '@spartacus/core';
import { CmsComponentData, LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront';
import { RecaptchaLoaderService } from 'ng-recaptcha';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AnonymousConsentsService } from '../../../core/anonymous-consents';
import { GlobalMessageService, GlobalMessageType } from '../../../core/global-message';
import { CmsMailFormComponent, CmsMailFormFieldComponent, CmsMailFormType } from '../../../core/model';
import { ExecuteRecaptchaEvent, RecaptchaCompleteEvent } from '../../../core/user/events/user.events';
import { GenerateLeadEvent } from '../../../events/misc';
import { LegalModalData } from '../../../shared/components/legal-modal/legal-modal.component';
import { DeferredRecaptchaService } from '../../../shared/services/recaptcha/deferred-recaptcha.service';
import { EmailValidator } from '../../../shared/validators/email-validator';
import { NoHashValidator } from '../../../shared/validators/no-hash-validator';
import { CmsMailFormFieldService, CmsMailFormService } from './base/services';

@Component({
  selector: 'py-mail-form',
  templateUrl: './mail-form.component.html',
  styleUrls: ['./mail-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailFormComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    public cmsComponent: CmsComponentData<CmsMailFormComponent>,
    private formBuilder: UntypedFormBuilder,
    private cmsMailFormFieldService: CmsMailFormFieldService,
    private cmsMailFormService: CmsMailFormService,
    private globalMessageService: GlobalMessageService,
    private cd: ChangeDetectorRef,
    private launchDialogService: LaunchDialogService,
    private anonymousConsentsService: AnonymousConsentsService,
    private eventService: EventService,
    @Inject(RecaptchaLoaderService) private recaptchaLoaderService: DeferredRecaptchaService
  ) {}
  isFormExpanded: Boolean = false;
  formData: CmsMailFormComponent;
  isLoading: boolean = false;
  group: UntypedFormGroup = new UntypedFormGroup({});
  formFields$: Observable<CmsMailFormFieldComponent[]>;
  isNewsletterForm: boolean = false;
  isContactMeForm: boolean = false;
  formSuccess: boolean = false;
  modal: any;
  files: File[] = [];

  private _subscriptions = new Subscription();

  get participants(): UntypedFormArray {
    return this.group.get('participants') as UntypedFormArray;
  }

  get buttonClass(): string {
    return (
      this.formData?.styleClasses
        ?.split(' ')
        ?.find((name) => name?.includes('cta-'))
        ?.replace('cta-', '') || 'btn-primary'
    );
  }

  @HostListener('click', ['$event'])
  onClick($event) {
    if ($event.target?.href?.includes('/terms')) {
      this.openMarketingModal();
      $event.preventDefault();
    }
  }

  openMarketingModal(): void {
    const modalData: LegalModalData = {
      component$: this.anonymousConsentsService.getTemplate('MARKETING').pipe(
        take(1),
        map((data) => {
          return {
            content: data.description,
            name: data.name,
          };
        })
      ),
    };

    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.LEGAL, undefined, modalData);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.formFields$ = this.cmsComponent.data$.pipe(
      filter((componentData) => typeof componentData?.fields === 'string'),
      switchMap((componentData) => {
        this.formData = componentData;

        if (!this.formData.styleClasses?.includes) {
          this.formData.styleClasses = '';
        }

        this.isNewsletterForm = this.formData.styleClasses?.includes('mailform-newsletter');
        this.isContactMeForm = this.formData.styleClasses?.includes('mailform-contact-me');

        if (this.formData.styleClasses?.includes('mailform-expanded-by-default')) {
          this.isFormExpanded = true;
        }

        return this.cmsMailFormFieldService.getCmsMailFormFields(this.formData.fields.split(' ').join(',')).pipe(
          map((res) => res.sort((a, b) => a.number - b.number)),
          tap((fieldsData) => this.addControls(fieldsData))
        );
      })
    );
  }

  ngAfterViewInit() {
    this.recaptchaLoaderService.init();
  }

  private addControls(fields): void {
    for (const f of fields) {
      if (f.name?.toLowerCase() === 'participant') {
        this.group.addControl('participants', new UntypedFormArray([]));
      } else {
        switch (f.type) {
          case CmsMailFormType.TEXT || CmsMailFormType.NUMBER:
            this.group.addControl(
              f.name,
              new UntypedFormControl('', this.decorateValidators(f, [NoHashValidator.noHashes, Validators.maxLength(50)]))
            );
            break;
          case CmsMailFormType.EMAIL:
            this.group.addControl(f.name, new UntypedFormControl('', this.decorateValidators(f, [EmailValidator.singleEmail])));
            break;
          case CmsMailFormType.LONGTEXT:
            this.group.addControl(f.name, new UntypedFormControl('', [NoHashValidator.noHashes]));
            break;
          case CmsMailFormType.CHECKBOX:
            this.group.addControl(f.name, new UntypedFormControl('', !!f.optional ? [] : [Validators.requiredTrue]));
            break;
        }
      }
    }
  }

  private decorateValidators(field, validators = []): Array<ValidatorFn> {
    if (!field.optional) {
      validators.push(Validators.required);
    }

    return validators;
  }

  onFormSubmit(): void {
    if (this.isNewsletterForm || this.isContactMeForm) {
      this.formSubmit();
    } else {
      this.isFormExpanded ? this.formSubmit() : (this.isFormExpanded = true);
    }
  }

  private formSubmit(): void {
    this.markFormGroupDirty(this.group);
    if (this.participants?.controls) {
      this.markParticipantsDirty();
    }

    if (this.group.valid) {
      this._subscriptions.add(
        this.eventService
          .get(RecaptchaCompleteEvent)
          .pipe(take(1))
          .subscribe((event) => {
            this.complete(event.ref);
            this.cd.detectChanges();
          })
      );

      this.eventService.dispatch(new ExecuteRecaptchaEvent());
      this.isLoading = true;
    }
  }

  private markParticipantsDirty() {
    (<any>Object).values(this.participants.controls).forEach((grp) => {
      this.markFormGroupDirty(grp);
    });
  }

  private markFormGroupDirty(group): void {
    (<any>Object).values(group.controls).forEach((control: AbstractControl) => {
      control.markAsDirty();
    });
  }

  removeParticipant(i: number): void {
    this.participants.removeAt(i);
  }

  addParticipant(): void {
    this.participants.push(
      this.formBuilder.group({
        name: ['', [Validators.required, NoHashValidator.noHashes]],
      })
    );
  }

  error(): void {
    this.globalMessageService.add(
      {
        raw: this.formData?.textFail,
      },
      GlobalMessageType.MSG_TYPE_ERROR
    );
  }

  success(): void {
    this.resetFormData();

    if (this.isNewsletterForm || this.isContactMeForm) {
      this.formSuccess = true;
    } else {
      this.globalMessageService.add(
        {
          raw: this.formData?.textSuccess,
        },
        GlobalMessageType.MSG_TYPE_CONFIRMATION
      );
    }
  }

  resetFormData(): void {
    this.isFormExpanded = false;
    while (this.participants && this.participants?.length !== 0) {
      this.participants.removeAt(0);
    }
  }

  complete(recaptchaKey): void {
    this.isLoading = false;

    if (this.group.value.participants) {
      this.group.value.participants = this.group.value.participants.map((item) => item.name).join() || null;
    }

    const filesFormData = new FormData();
    this.files.forEach((file) => filesFormData.append('files', file));

    this._subscriptions.add(
      this.cmsMailFormService
        .postCmsMailForm(
          JSON.stringify({
            ...this.group.value,
            id: this.formData?.uid,
          }),
          recaptchaKey,
          filesFormData
        )
        .pipe(take(1))
        .subscribe(
          () => this.success(),
          () => this.error()
        )
        .add(() => {
          this.isLoading = false;
          this.eventService.dispatch(new GenerateLeadEvent(this.group.value));
          this.group.reset();
          this.files = [];
          this.cd.detectChanges();
        })
    );
  }
}
